import React, { useState } from 'react';
import user_logo from '../../../assets/icons/user.svg';
import VideoModal from 'reusable/videopreview';
import video_icon from '../../../assets/icons/video.svg';
import { useTranslation } from 'react-i18next';

interface Submission {
  user?: {
    name: string;
    phone: string;
    profile_picture?: string;
  };
  data: string;
  evidence: string;
  testing_time: string;
}

interface ExceedAlcoholLimitsTableProps {
  submissions: Submission[];
}

const ExceedAlcoholLimitsTable: React.FC<ExceedAlcoholLimitsTableProps> = ({
  submissions,
}) => {
  const { t, i18n } = useTranslation('alcohol');
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoLink, setCurrentVideoLink] = useState('');

  const handleVideoClick = (videoLink: string) => {
    if (videoLink) {
      setCurrentVideoLink(videoLink);
      setShowVideo(true);
    } else {
      console.log('No video available');
    }
  };

  const formatDate = (datetime: string) => {
    const date = new Date(datetime);
    
    if (i18n.language === 'jp') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); 
        const year = date.getFullYear();

        return {
            time: date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false }),
            date: `${year}/${month}/${day}`
        };
    } else {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const day = date.getDate().toString().padStart(2, '0'); 
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        const localTimeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

        return {
            time: localTimeString,
            date: `${day} ${months[monthIndex]} ${year}`
        };
    }
  };

  const getColorClass = (dataValue: string) => {
    const value = parseFloat(dataValue);
    if (value >= 0.15) {
      return 'text-red-500 bg-red-100 px-2 rounded-md flex items-center justify-center w-24';
    } else if (value <= 0.0) {
      return 'text-green-500 bg-green-100 px-2 rounded-md flex items-center justify-center w-24';
    } else {
      return 'text-yellow-500 bg-yellow-100 px-2 rounded-md flex items-center justify-center w-24';
    }
  };

  const getFileExtension = (url: string) => {
    const extension = url.split('.').pop();
    if (extension) {
      return extension.split('?')[0];
    }
    return '';
  };

  return (
    <>
      {showVideo && (
        <VideoModal
          videoLink={currentVideoLink}
          onClose={() => setShowVideo(false)}
        />
      )}
      <main className="flex flex-col my-4">
        <div className="grid grid-cols-5 gap-4 p-2 bg-primary/50 rounded-md">
          <h6 className="w-40">{t('table.driver-name')}</h6>
          <h6 className="w-24">{t('table.test-result')}</h6>
          <h6 className="w-40">{t('table.video-submission')}</h6>
          <h6 className="w-20">{t('table.time')}</h6>
          <h6 className="w-40">{t('table.date')}</h6>
        </div>
        {submissions.length === 0 ? (
          <div className="text-center py-2 mt-10">{t('table.no-data')}</div>
        ) : (
          submissions.map((submission, index) => (
            <div key={index} className="grid grid-cols-5 gap-4 p-2 border-b">
              <div className="flex space-x-2 items-center">
                <img
                  src={submission.user?.profile_picture || user_logo}
                  alt="User"
                  className="h-10 w-10 object-cover rounded-full"
                />
                <div>
                  <div className="w-32">
                    {submission.user?.name || t('table.no-data')}
                  </div>
                  <div className="text-caption w-32">
                    {submission.user?.phone || t('table.no-data')}
                  </div>
                </div>
              </div>
              <div className={getColorClass(submission.data)}>
                {submission.data} mg/L
              </div>
              <div
                className={`w-40 border flex items-center rounded-md justify-center ${
                  submission.evidence ? 'cursor-pointer' : ''
                }`}
                onClick={() =>
                  submission.evidence && handleVideoClick(submission.evidence)
                }
              >
                {submission.evidence ? (
                  <>
                    <img src={video_icon} alt="Video Icon" className="mr-2" />
                    {`${submission.evidence.slice(-5)} (.${getFileExtension(
                      submission.evidence
                    )})`}
                  </>
                ) : (
                  <span className="text-gray-500">No Video</span>
                )}
              </div>
              <div className="w-20">
                {formatDate(submission.testing_time).time}
              </div>
              <div className="w-40">
                {formatDate(submission.testing_time).date}
              </div>
            </div>
          ))
        )}
      </main>
    </>
  );
};

export default ExceedAlcoholLimitsTable;
